import { useContext } from "react"
import { GetServerSideProps } from "next"
import { http } from "src/services/http"
import { ApiUserData } from "src/models/user/ApiUserData"
import { UserPageLayout } from "src/components/userPage/layout"
import RewardRequestForm, {
  FormParams,
} from "src/components/userPage/RewardRequest/RewardRequestForm"
import RewardRequestNotAuth from "src/components/userPage/RewardRequest/RewardRequestNotAuth"
import RewardRequestDisabled from "src/components/userPage/RewardRequest/RewardRequestDisabled"
import { useTieupToast } from "../../../../components/hooks/useTieupToast"
import { useCommonConfirmDialog } from "src/components/hooks/useCommonConfirmDialog"
import { useLoading } from "src/components/hooks/useLoading"
import useSWR from "swr"
import { fetchUserReceivingGiftConditions } from "src/models/user/fetchUserReceivingGiftConditions"
import { postRewardRequest } from "src/models/RewardRequest/postRewardRequest"
import { globalStoreContext } from "src/store/GlobalStore"
import { BarLoader } from "react-spinners"
import colors from "src/config/color"
import { useRouter } from "next/router"
import routes from "routes"

import styles from "./index.module.scss"
type Props = {
  user: ApiUserData
}

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
  res,
}) => {
  const { display_name } = query
  /** display_nameがなかったら404ページにリダイレクトする */
  if (!display_name) {
    res?.writeHead(302, { Location: "/404" })
    res?.end()
  }

  const user = await http
    .get<ApiUserData>(`/v1/user/${display_name}`)
    .then((v) => v.data)
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error(error)
    })

  // ユーザーが退会済み(404)、または退会申請中の場合は404に飛ばす
  if (!user || user.will_resign_datetime !== null) {
    return { notFound: true }
  }

  return { props: { user } }
}

const UserRequest: React.FC<Props> = ({ user }) => {
  const {
    globalStore: {
      user: { clientState, apiUserData },
    },
  } = useContext(globalStoreContext)

  const router = useRouter()

  const { addToastError } = useTieupToast()

  const { confirmDialog } = useCommonConfirmDialog()

  const { fullScreenLoading } = useLoading()

  // ギフトを送れない人にはリクエストも送れないので
  // リクエストの可否を判別するのにギフト送信可否APIを使う
  const { data: userReceivingGiftConditions, error: getConditionError } =
    useSWR(
      `/v1/gift/available/${user.id}`,
      () => fetchUserReceivingGiftConditions(user.id),
      { revalidateOnMount: true, revalidateOnFocus: false },
    )

  if (getConditionError) {
    addToastError(
      "リクエストの送信可否条件の取得に失敗しました。暫く後でお試し下さい",
    )
  }

  const handleSubmit = (params: FormParams) => {
    confirmDialog(
      `この内容で欲しい物を依頼しますか？ `,
      "はい",
      "いいえ",
      async () => {
        fullScreenLoading(true)

        await postRewardRequest({
          user,
          ...params,
        })
          .then(() => {
            // APIにリクエスト完了後、完了画面に遷移
            router.push(routes.userRequestCompleted(user.display_name))
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error(error)

            if (error.response.status === 400) {
              addToastError("自分にはリクエストを送れません")
            } else if (error.response.status === 404) {
              addToastError("リクエストを送るユーザーが存在しません")
            } else if (error.response.status === 412) {
              addToastError("リクエストを送れないユーザーです")
            } else {
              addToastError("処理に失敗しました")
            }
          })
        fullScreenLoading(false)
      },
    )
  }

  // ローディング
  if (clientState === null || userReceivingGiftConditions === undefined) {
    return (
      <UserPageLayout user={user} currentContent="Request">
        <div className={styles.loader}>
          <BarLoader color={colors.spectrum.tieupTheme} />
        </div>
      </UserPageLayout>
    )
  }

  if (!userReceivingGiftConditions.canReceive()) {
    return (
      // リクエストを送れないユーザーだった場合の表示
      <UserPageLayout user={user} currentContent="Request">
        <RewardRequestDisabled />
      </UserPageLayout>
    )
  }

  return (
    <>
      <UserPageLayout user={user} currentContent="Request">
        {clientState === 2 && apiUserData !== null ? (
          <RewardRequestForm
            onSubmit={handleSubmit}
            isHiddenSubmitButton={apiUserData.id === user.id}
          />
        ) : (
          <RewardRequestNotAuth user={user} />
        )}
      </UserPageLayout>
      )
    </>
  )
}

export default UserRequest
